import { EVENT_MODE_NORMAL, EVENT_MODE_PUNCHUP } from 'utils/constants';
import {
	EVENT_SCANS,
	LOTTERY,
	NORMAL_DASHBOARD_HIGHLIGHTS,
	PAGE_VIEWS,
	REG_DASHBOARD_HIGHLIGHTS,
	TICKETS_COMPED,
	TOTAL_ATTENDEES,
	TOTAL_PAYOUT,
} from 'features/common/components/HighlightsCollection/constants';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

// The green box shape (it owns `entryLabel`) — this row renders through the green tile, not the
// legacy HighlightCard.
import { BoxProps } from 'features/common/components/Punchup/composites/tiles/PunchupHighlightsCollection/types';
import { EventData } from 'features/common/types';
import { IdParamsType } from 'utils/types';
import {
	PunchupHighlightsCollection,
	PunchupLineupCard,
} from 'features/common/components/Punchup/composites/tiles/PunchupHighlightsCollection';
import { ROUTES } from 'features/routes';
import { toSigDigits } from 'utils/utils';
import { useComediansFromHost } from './useComediansFromHost';
import { useHighlights } from 'features/common/hooks/useHighlights';
import { useTranslation } from 'react-i18next';

interface EventHighlightsProps {
	event: EventData | null;
	/** When true, renders default tiles (no Lottery) in skeleton loading state — used when
	 * the event API call has failed. Tiles are visible but inactive (no stats, no onClick). */
	isDisabled?: boolean;
}

const EventHighlights = ({ event, isDisabled = false }: EventHighlightsProps) => {
	const { push } = useHistory();
	const { id } = useParams<IdParamsType>();
	const { t } = useTranslation();
	// Stat tiles spin on `isFetching` (covers both first load and a background refetch after a mutation
	// invalidates highlights) — the spinner-not-0 / refetch-spinner contract for the dashboard tiles.
	const { eventHighlights, isFetching } = useHighlights(id);
	// The show lineup lives in the PunchupLive host, not in any tixologi API — it arrives by
	// postMessage. `null` = this host never pushed one (older host), so no lineup tile at all.
	const comedians = useComediansFromHost();

	// Registration-mode shows keep their single-tile row; everything else gets the full green strip.
	const isNormalRow = isDisabled || !!(event && [EVENT_MODE_NORMAL, EVENT_MODE_PUNCHUP].includes(event.mode));

	// Derived in render (no state mirror). Each box is a FRESH clone of the source constant: the source
	// arrays hold module-level singleton card objects, so mutating them in place (the prior effect did)
	// bled one event's stat/onClick into the next mount. Building from the source — not the previous
	// result — also lets a filtered-out tile (Lottery) reappear when inputs change.
	const boxCollection = useMemo(() => {
		const source: BoxProps[] = isNormalRow ? NORMAL_DASHBOARD_HIGHLIGHTS : REG_DASHBOARD_HIGHLIGHTS;

		const boxCollections: BoxProps[] = [];
		for (const base of source) {
			const box: BoxProps = { ...base };
			box.isLoading = isFetching || isDisabled;

			if (isDisabled) {
				// Skeleton state: show all default tiles except Lottery; no actions
				if (box.property === LOTTERY) continue;
			} else if (box.property === TOTAL_PAYOUT) {
				box.stat = toSigDigits(eventHighlights?.total_payout || 0);
				box.onClick = () => push(generatePath(ROUTES.ORDERS_BY_EVENT_PATH, { id }));
			} else if (box.property === TOTAL_ATTENDEES) {
				box.stat = eventHighlights?.total_attendees || 0;
				box.onClick = () => push(generatePath(ROUTES.ATTENDEES, { id }));
			} else if (box.property === PAGE_VIEWS) {
				box.stat = eventHighlights?.page_views || 0;
				box.onClick = () => push(generatePath(ROUTES.ANALYTICS_BY_EVENT_PATH, { id }));
			} else if (box.property === TICKETS_COMPED) {
				box.stat = eventHighlights?.tickets_comped || 0;
				box.onClick = () => push(generatePath(ROUTES.COMPED_TICKETS, { id }));
			} else if (box.property === LOTTERY) {
				if (!event?.ticketTypes.some((ticket) => ticket.isLottery)) continue;
				box.stat = eventHighlights?.lottery_entries || 0;
				box.onClick = () => push(generatePath(ROUTES.LOTTERY, { id }));
			} else if (box.property === EVENT_SCANS) {
				box.stat = eventHighlights?.nums_of_scans || 0;
				// Navigates to the Scanned Tickets log (PUN-4942 req. 5a). This reverses TIC-308, which left the
				// tile count-only because green has no gate/scanner config UI — the door log is useful on its own,
				// so the count is now a way in rather than a dead end.
				box.onClick = () => push(generatePath(ROUTES.SCANS, { id }));
			}
			boxCollections.push(box);
		}
		return boxCollections;
	}, [eventHighlights, isFetching, isDisabled, isNormalRow, event, id, push]);

	// The lineup section lives in the host page, below the iframe — so the tile asks the host to open
	// and scroll to it (PUN-4942 req. 3) instead of navigating this frame anywhere.
	const scrollToComedians = useCallback(() => {
		window.parent.postMessage({ action: 'scroll-to-comedians' }, '*');
	}, []);

	// Memoised: PunchupHighlightsCollection is memo-wrapped, and a fresh child node every render
	// would defeat it.
	const lineupCard = useMemo(
		() =>
			comedians && isNormalRow ? (
				<PunchupLineupCard
					members={comedians}
					title={t('dashboard-highlights-showLineup-green', 'Show Lineup')}
					entryLabel={t('highlights-view-lineup-green', 'See all comedians')}
					onClick={scrollToComedians}
					dataCy="show-lineup-card"
				/>
			) : null,
		[comedians, isNormalRow, scrollToComedians, t]
	);

	return <PunchupHighlightsCollection boxCollection={boxCollection}>{lineupCard}</PunchupHighlightsCollection>;
};

export { EventHighlights };
